import { Button, Col, Flex, Row, Typography } from "antd";
import clsx from "clsx";
import { Can } from "@/shared/lib/casl";
import { UserBubble } from "@shared/ui/user-bubble";
import { fallbackString, formatMoscowDatetime } from "@shared/utils";
import { JournalCryptographyWrapper } from "../journal-cryptography-wrapper";
import { statusIcons } from "./model/status-icons";
import "./journal-signer.scss";

const { Text } = Typography;

interface JournalSignerProps {
  title?: string;
  signerLabel?: string;
  currentItem: any;
  signerFio: string;
  isSignerAwaiting: boolean;
  sectionListId?: string;
  itemNoVersionId: string;
  localUser: any;
  signingString: string;
  isSigningPending: boolean;
  getSigningXml: (params: any) => Promise<string>;
  setSigningString: (xml: string) => void;
  withSignedString: (params: any, xml: string, mchdId?: string) => void;
  invalidateItem: () => void;
  openReject: () => void;
  canAction?: any;
}

export const JournalSigner = ({
  title = "Подписант",
  signerLabel = "Уполномоченный представитель",
  currentItem,
  signerFio,
  isSignerAwaiting,
  sectionListId,
  itemNoVersionId,
  localUser,
  signingString,
  isSigningPending,
  getSigningXml,
  setSigningString,
  withSignedString,
  invalidateItem,
  openReject,
  canAction = "section_1",
}: JournalSignerProps) => {
  const isRejected = currentItem?.status === "REJECTED";
  const isSigned = currentItem?.status === "SIGNED";
  const mchdNumber =
    currentItem?.signer?.mchdId ||
    currentItem?.inspectionEmployee?.mchdId ||
    currentItem?.defectFixingInspectionEmployee?.mchdId ||
    currentItem?.mchdId;

  return (
    <div
      className={clsx("journal-signer", isRejected && "journal-signer--rejected", isSigned && "journal-signer--signed")}
    >
      <div className="journal-signer__title">{title}</div>
      <Row gutter={[0, 8]} justify="space-between">
        <Col span={12}>
          <Text type="secondary">{signerLabel}</Text>
        </Col>
        <Col span={12} className="journal-signer__col-right">
          <Flex align="center" gap="8px" style={{ justifyContent: "flex-end" }}>
            <UserBubble userId={currentItem?.signer?.userId || ""}>
              <Typography.Text style={{ color: "#4169E1", cursor: "pointer" }}>
                {fallbackString(signerFio)}
              </Typography.Text>
            </UserBubble>
            {currentItem?.status !== "NEW" && (
              <div className="journal-signer__status-icon-slot">
                {statusIcons[currentItem?.status as keyof typeof statusIcons]}
              </div>
            )}
          </Flex>
          <div>{fallbackString(currentItem?.signer?.position)}</div>
          {isSignerAwaiting && sectionListId && (
            <div className="journal-signer__actions">
              <Can I="sign" a={canAction as any}>
                <JournalCryptographyWrapper
                  currentUser={localUser as any}
                  signContent={signingString}
                  onSuccess={(xml, _proxyId, mchdId) =>
                    withSignedString(
                      {
                        sectionListId,
                        itemNoVersionId,
                        signerEmployeeId: localUser?.user_id || "",
                        onSuccess: () => invalidateItem(),
                      },
                      xml,
                      mchdId,
                    )
                  }
                >
                  {({ onOpen }: any) => (
                    <Button
                      type="primary"
                      loading={isSigningPending}
                      onClick={async () => {
                        try {
                          const xml = await getSigningXml({
                            sectionListId,
                            itemNoVersionId,
                            signerEmployeeId: localUser?.user_id || "",
                          });
                          setSigningString(xml);
                          onOpen();
                        } catch (e) {
                          // eslint-disable-next-line no-console
                          console.error(e);
                        }
                      }}
                    >
                      Подписать
                    </Button>
                  )}
                </JournalCryptographyWrapper>
              </Can>
              <Can I="sign" a={canAction as any}>
                <Button type="dashed" onClick={openReject}>
                  Отклонить
                </Button>
              </Can>
            </div>
          )}
        </Col>
      </Row>

      {isRejected && (() => {
        const comments = currentItem?.comments || [];
        const validComments = comments.filter(
          (c: any) => {
            const commentText = c?.commentText ?? c?.comment;
            return commentText && commentText.toString().trim();
          },
        );
        const commentText = validComments.length > 0
          ? validComments.map((c: any) => c?.commentText ?? c?.comment).join("\n")
          : currentItem?.comment;

        return (
          <div className="journal-signer__status-info journal-signer__status-info--rejected">
            <div className="journal-signer__status-header">
              <div className="journal-signer__status-text">
                <div className="journal-signer__status-icon">
                  {statusIcons.REJECTED}
                </div>
                <span className="journal-signer__status-title">
                  Отклонено подписание
                </span>
              </div>
              <div className="journal-signer__status-date">
                {formatMoscowDatetime(currentItem?.updateDate)}
              </div>
            </div>
            {commentText && (
              <div className="journal-signer__status-comment">
                {commentText}
              </div>
            )}
          </div>
        );
      })()}

      {isSigned && (
        <div className="journal-signer__status-info journal-signer__status-info--signed">
          <div className="journal-signer__status-header">
            <div className="journal-signer__status-text">
              <div className="journal-signer__status-icon" />
              <span className="journal-signer__status-title">Подписано</span>
            </div>
            <div className="journal-signer__status-date">
              {formatMoscowDatetime(currentItem?.updateDate)}
            </div>
          </div>
          {mchdNumber ? (
            <div className="journal-signer__status-mchd">
              Доверенность № {mchdNumber}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
